import React, { useEffect, useMemo, useState } from "react";
import { ProductModificationDialog, Product } from "@semo/shared-product";
import { ApiError, CategoryTreeNodeDto } from "@semo/api-contracts";

import { useCategoryTree } from "../categories/hooks/useCategoryTree";
import { CategoryTopNav } from "./CategoryTopNav";
import { VendorProductTable } from "./VendorProductTable";
import { useMyStorefrontPrices } from "../items/application/hooks/useMyStorefrontPrices";
import { StorefrontOfferingsPanel } from "./StorefrontOfferingsPanel";
import { useProducts } from "../items/application/hooks/useProducts";
import {
  useVendorProductList,
  type VendorSellingStatus,
} from "../items/application/hooks/useVendorProductList";
import { Button } from "@semo/shared-ui";
import {
  Plus,
  List,
  FileSpreadsheet,
  AlignLeft,
  AlignJustify,
  ChevronDown,
  Info,
  Loader2,
} from "@semo/icons";
import { Badge } from "@semo/shared-ui";
import { Input } from "@semo/shared-ui";
import { Checkbox } from "@semo/shared-ui";
import { Switch } from "@semo/shared-ui";
import { Label } from "@semo/shared-ui";
import { RadioGroup, RadioGroupItem } from "@semo/shared-ui";
import { toast } from "sonner";
import { SupplierItemRequestDialog } from "./SupplierItemRequestDialog";
import { SupplierRequestHistoryDialog } from "./SupplierRequestHistoryDialog";
import { SupplierPriceUploadDialog } from "./SupplierPriceUploadDialog";
import { ProductSearchBar } from "@semo/shared-product";
import { Popover, PopoverContent, PopoverTrigger } from "@semo/shared-ui";
import { ScrollArea } from "@semo/shared-ui";
import { AdvancedPagination } from "@semo/shared-ui";
import { cn } from "@semo/shared-ui";
import { ToggleGroup, ToggleGroupItem } from "@semo/shared-ui";

const ITEMS_PER_PAGE = 100;

interface UserProductManagementProps {
  autoQuoteEnabled: boolean;
  onToggleAutoQuote: (checked: boolean) => void;
  kakaoNotificationEnabled: boolean;
  onToggleKakaoNotification: (checked: boolean) => void;
  isUpdatingSettings: boolean;
  /**
   * DEV 전용: 제공되면 staging API 대신 이 샘플 상품으로 동작한다.
   * (가격/판매상태 변경은 로컬 상태로만 반영) — /dev/preview?tab=products 미리보기용.
   */
  previewProducts?: Product[];
  /** DEV 전용: 제공되면 카테고리 트리도 이 샘플로 대체 (상단 탭/LNB 미리보기용). */
  previewCategoryTree?: CategoryTreeNodeDto[];
}

type SellingFilter = "all" | "selling" | "stopped";

const isSellingFilter = (v: string): v is SellingFilter =>
  v === "all" || v === "selling" || v === "stopped";

/** 판매상태 필터 → 서버 `sellingStatus`. */
const toSellingStatus = (filter: SellingFilter): VendorSellingStatus =>
  filter === "selling" ? "active" : filter === "stopped" ? "inactive" : "all";

/** 서버 검색은 2글자부터다. 그보다 짧으면 검색하지 않고 제목도 바꾸지 않는다. */
const MIN_SEARCH_LENGTH = 2;

/** 서버가 준 한국어 사유가 있으면 그것을, 없으면 기본 문구를. */
const serverMessageOr = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError && error.isClientError && error.userMessage) {
    return error.userMessage;
  }
  return fallback;
};

type Density = "compact" | "default";
const isDensity = (v: string): v is Density =>
  v === "compact" || v === "default";

export const UserProductManagement: React.FC<UserProductManagementProps> = ({
  // 자동견적·알림톡은 공급사 단위 설정이고, 임베드 신원 정보에서 빠졌다(05-13 결정 —
  // «다른 곳에서 관리»). 현재값을 알 수 없는 스위치를 여기 두면 또 «켜진 척» 이 되므로
  // 그리지 않는다(GS 검수 V-07). props 는 상위 호환을 위해 인터페이스에만 남긴다.
  previewProducts,
  previewCategoryTree,
}) => {
  const isPreview = !!previewProducts;

  const {
    categoryTree: apiCategoryTree,
    flatCategories: apiFlatCategories,
    mode: categoryMode,
    setMode: setCategoryMode,
  } = useCategoryTree();

  const categoryTree =
    isPreview && previewCategoryTree ? previewCategoryTree : apiCategoryTree;

  // Flat list (id + name) used for selected-category lookup / reset guard
  const flatCategories = useMemo(() => {
    if (isPreview && previewCategoryTree) {
      const out: { id: string; name: string }[] = [];
      const walk = (nodes: CategoryTreeNodeDto[]) =>
        nodes.forEach((n) => {
          out.push({ id: n.id, name: n.name });
          walk(n.children);
        });
      walk(previewCategoryTree);
      return out;
    }
    return apiFlatCategories;
  }, [isPreview, previewCategoryTree, apiFlatCategories]);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    "1",
  );

  const [showOnlyMyProducts, setShowOnlyMyProducts] = useState(
    categoryMode === "dealing",
  );
  const [sellingFilter, setSellingFilter] = useState<SellingFilter>("all");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [density, setDensity] = useState<Density>(() => {
    if (typeof window !== "undefined") {
      const saved = window.localStorage.getItem("semo_vendor_product_density");
      return saved === "default" ? "default" : "compact";
    }
    return "compact";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("semo_vendor_product_density", density);
    }
  }, [density]);

  // Pagination State
  const [page, setPage] = useState(1);

  // Debounce Search Term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /** 실제로 서버에 보내는 검색어. 1글자는 검색하지 않는다(제목도 그대로). */
  const effectiveSearch =
    debouncedSearchTerm.trim().length >= MIN_SEARCH_LENGTH
      ? debouncedSearchTerm.trim()
      : "";
  const isSearchTooShort = searchTerm.trim().length === 1;

  /**
   * 판매상태 필터는 «내 상품» 에만 뜻이 있다 — 고르면 서버의 내 상품 목록을 그 상태로 받는다.
   * 예전에는 받아 온 100줄 안에서만 걸러 «결과 없음 + 전체 406,437개» 가 같이 보였다(V-08).
   */
  const isSellingFiltered = sellingFilter !== "all";
  const queryMyProducts = showOnlyMyProducts || isSellingFiltered;

  useEffect(() => {
    setPage(1);
  }, [selectedCategoryId, effectiveSearch, showOnlyMyProducts, sellingFilter]);

  const productOptions = useMemo(
    () => ({
      onlyMyProducts: queryMyProducts,
      onlyMyCategories: categoryMode === "dealing",
      search: effectiveSearch || undefined,
      // 내 상품 «전체» 는 판매중·판매중지 모두다(서버 기본은 판매중만).
      sellingStatus: queryMyProducts ? toSellingStatus(sellingFilter) : undefined,
      limit: ITEMS_PER_PAGE,
      offset: (page - 1) * ITEMS_PER_PAGE,
    }),
    [queryMyProducts, categoryMode, effectiveSearch, sellingFilter, page],
  );

  const {
    products: apiProducts,
    pagination,
    isFetching: isProductsFetching,
  } = useVendorProductList(selectedCategoryId, productOptions);
  const { updateSettings } = useProducts();

  /** 목록을 다시 받아오는 중. 필터 조작에 즉시 반응을 주는 데 쓴다. */
  const isFilterBusy = !isPreview && isProductsFetching;

  // --- Preview (local) products: lets /dev/preview?tab=products run without the API ---
  const [localProducts, setLocalProducts] = useState<Product[]>(
    previewProducts ?? [],
  );
  const products = isPreview ? localProducts : apiProducts;

  useEffect(() => {
    if (selectedCategoryId !== "1" && flatCategories.length > 0) {
      const exists = flatCategories.some((c) => c.id === selectedCategoryId);
      if (!exists) {
        setSelectedCategoryId("1");
      }
    }
  }, [flatCategories, selectedCategoryId]);

  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [brandSearch, setBrandSearch] = useState("");

  // Row-selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Dialog States
  const [isSupplierRequestOpen, setIsSupplierRequestOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPriceUploadOpen, setIsPriceUploadOpen] = useState(false);
  const [isModificationDialogOpen, setIsModificationDialogOpen] =
    useState(false);
  const [modificationTargetProduct] = useState<Product | null>(null);

  const [pendingTogglingProductIds, setPendingTogglingProductIds] = useState<
    Set<string>
  >(new Set());

  const selectedCategory = flatCategories.find(
    (c) => c.id === selectedCategoryId,
  );

  /**
   * 브랜드 필터 — **지금 불러온 쪽 안에서만** 거른다. 서버에 브랜드 조건이 없어(검색 순위
   * 쪽 코드라 손대지 않는다) 화면이 그 사실을 그대로 밝힌다: 개수는 «이 쪽 N개 중 M개», 안내
   * 문구에 «지금 보이는 쪽 안에서만» 을 적는다(V-08). 판매상태는 서버가 거른다(위).
   *
   * 미리보기(로컬 샘플)는 서버가 없으므로 판매상태도 여기서 거른다.
   */
  const isBrandFiltered = selectedBrands.size > 0;
  const visibleProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesBrand = !isBrandFiltered
        ? true
        : Boolean(p.brand && selectedBrands.has(p.brand)) ||
          Boolean(p.manufacturer && selectedBrands.has(p.manufacturer));
      const matchesSelling =
        !isPreview || sellingFilter === "all"
          ? true
          : sellingFilter === "selling"
            ? !!p.isSelling
            : !p.isSelling;
      return matchesBrand && matchesSelling;
    });
  }, [products, selectedBrands, isBrandFiltered, sellingFilter, isPreview]);

  // 마스터가 허용한 쇼핑몰의 단가 열. 허용이 없으면 열도 안 생긴다.
  const {
    storefronts,
    priceOf,
    isAllowed: isStorefrontAllowed,
    requestOffering,
    setPrice: setStorefrontPrice,
  } = useMyStorefrontPrices(visibleProducts.map((p) => p.id));

  /**
   * 몰 모드.
   *
   * null 이면 지금까지처럼 «전 품목» 표다. 몰을 고르면 그 몰에 승인된 것만 보는
   * 화면으로 통째로 갈아 끼운다 — 수만 줄짜리 표에 필터를 하나 더 다는 대신
   * 축을 바꾸는 쪽이 맞다(승인분은 수십~수천 건이고 표 전체와 자릿수가 다르다).
   */
  const [activeStorefrontId, setActiveStorefrontId] = useState<string | null>(
    null,
  );
  const activeStorefront = storefronts.find(
    (sf) => sf.storefrontId === activeStorefrontId,
  );

  /**
   * 공급가만 저장한다 — **판매 상태는 바꾸지 않는다**(GS 검수 V-07).
   *
   * 서버는 처음 단가를 넣을 때 `isSelling` 이 없으면 판매중으로 만든다(씨마켓 외부 API 가
   * 같은 함수를 써서 서버 기본값은 그대로 둔다). 그래서 아직 판매중이 아닌 행은
   * `isSelling: false` 를 함께 보내 «단가만 넣었는데 판매중» 이 되지 않게 한다. 화면 안내도
   * «단가를 입력하고 ‘판매 상태’를 켜주세요» 다.
   */
  const handlePriceChange = async (productId: string, price: number) => {
    const target = products.find((p) => p.id === productId);
    const isSellingNow = !!target?.isSelling;

    if (isPreview) {
      setLocalProducts((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, myPrice: price } : p)),
      );
      return;
    }
    try {
      await updateSettings.mutateAsync({
        id: productId,
        data: isSellingNow ? { price } : { price, isSelling: false },
      });
      toast.success(
        isSellingNow
          ? "공급가를 저장했습니다."
          : "공급가를 저장했습니다. 판매하려면 ‘판매 상태’를 켜주세요.",
      );
    } catch (error) {
      toast.error(serverMessageOr(error, "가격 수정에 실패했습니다."));
    }
  };

  const toggleSelling = async (
    productId: string,
    currentStatus: boolean,
    price: number,
  ) => {
    if (pendingTogglingProductIds.has(productId)) return;

    if (!currentStatus && price <= 0) {
      toast.error("공급가를 1원 이상 입력해야 판매할 수 있습니다.");
      return;
    }

    if (isPreview) {
      setLocalProducts((prev) =>
        prev.map((p) =>
          p.id === productId
            ? { ...p, isSelling: !currentStatus, myPrice: price }
            : p,
        ),
      );
      toast.success(
        !currentStatus
          ? "판매 상품으로 설정되었습니다."
          : "판매 중지 처리되었습니다.",
      );
      return;
    }

    try {
      setPendingTogglingProductIds((prev) => new Set(prev).add(productId));
      await updateSettings.mutateAsync({
        id: productId,
        data: { isSelling: !currentStatus, price },
      });
      toast.success(
        !currentStatus
          ? "판매 상품으로 설정되었습니다."
          : "판매 중지 처리되었습니다.",
      );
    } catch {
      toast.error("판매 상태 변경에 실패했습니다.");
    } finally {
      setPendingTogglingProductIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // Row selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(
      checked ? new Set(visibleProducts.map((p) => p.id)) : new Set(),
    );
  };

  // Available brands in current context
  const availableBrands = useMemo(() => {
    const brandSet = new Set<string>();
    products.forEach((p) => {
      if (p.brand) brandSet.add(p.brand);
      if (p.manufacturer) brandSet.add(p.manufacturer);
    });
    return Array.from(brandSet).sort();
  }, [products]);

  const toggleBrand = (brand: string) => {
    const newSet = new Set(selectedBrands);
    if (newSet.has(brand)) newSet.delete(brand);
    else newSet.add(brand);
    setSelectedBrands(newSet);
  };

  // 브랜드 드롭다운: 검색어로 거른 목록 + 트리거 라벨
  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase();
    return q
      ? availableBrands.filter((b) => b.toLowerCase().includes(q))
      : availableBrands;
  }, [availableBrands, brandSearch]);

  const selectedBrandList = Array.from(selectedBrands);
  const brandLabel =
    selectedBrandList.length === 0
      ? "선택"
      : selectedBrandList.length === 1
        ? selectedBrandList[0]
        : `${selectedBrandList[0]} 외 ${selectedBrandList.length - 1}`;

  // DEV 미리보기: 실제론 상품이 수만 개라 페이지가 여러 개 → 페이지네이션 UI가 보이도록 가짜 총계 사용
  const PREVIEW_TOTAL = 54395;
  const totalCount = isPreview ? PREVIEW_TOTAL : pagination.totalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const selectedCount = selectedIds.size;

  // 제목·개수 — 화면에 보이는 목록과 같은 조건이어야 한다(V-08·V-15).
  const isCategorySelected = !!selectedCategory && selectedCategory.id !== "1";
  const titleBase = effectiveSearch
    ? `"${effectiveSearch}" 검색 결과`
    : isCategorySelected
      ? selectedCategory.name
      : queryMyProducts
        ? "내 상품"
        : "전체 상품";
  const sellingLabel =
    sellingFilter === "selling"
      ? "판매중"
      : sellingFilter === "stopped"
        ? "판매중지"
        : null;
  const titleText = sellingLabel ? `${titleBase} · ${sellingLabel}` : titleBase;

  const baseDescription = effectiveSearch
    ? `${isCategorySelected ? `'${selectedCategory.name}' 내에서` : queryMyProducts ? "내 상품 중에서" : "전체 상품 중에서"} 검색된 결과입니다.`
    : isCategorySelected
      ? queryMyProducts
        ? `${selectedCategory.name} 카테고리의 내 상품입니다.`
        : `${selectedCategory.name} 카테고리 내 전체 물품입니다.`
      : queryMyProducts
        ? "판매자님이 공급가를 등록한 내 상품입니다."
        : "등록된 전체 마스터 상품 목록입니다.";
  const filterNotes = [
    sellingFilter === "selling" ? "판매중인 내 상품만 보여줍니다." : null,
    sellingFilter === "stopped" ? "판매를 멈춘 내 상품만 보여줍니다." : null,
    isBrandFiltered
      ? "브랜드는 지금 보이는 쪽 안에서만 거릅니다 — 다른 쪽은 쪽을 넘겨 확인하세요."
      : null,
  ].filter((note): note is string => note !== null);

  const emptyState =
    isBrandFiltered && products.length > 0
      ? {
          title: "이 쪽에는 선택한 브랜드의 상품이 없습니다.",
          description: "다른 쪽으로 넘기거나 브랜드 선택을 해제해보세요.",
        }
      : sellingFilter === "selling"
        ? {
            title: "판매중인 상품이 없습니다.",
            description:
              "공급가를 입력하고 ‘판매 상태’를 켜면 여기에 나타납니다.",
          }
        : sellingFilter === "stopped"
          ? {
              title: "판매중지한 상품이 없습니다.",
              description: "‘판매 상태’를 끈 내 상품이 여기에 나타납니다.",
            }
          : undefined;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Top category navigation (replaces left sidebar) */}
      <CategoryTopNav
        categoryTree={categoryTree}
        selectedId={selectedCategoryId}
        onSelect={setSelectedCategoryId}
        mode={categoryMode}
        setMode={setCategoryMode}
      />

      {/*
        쇼핑몰 전환.
        마스터가 열어 준 몰이 있을 때만 나타난다. 「가격 미입력」 배지가 곧
        «승인은 받았는데 안 팔리고 있다» 는 신호다.
      */}
      {storefronts.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 bg-background px-3 py-2 sm:px-6">
          <button
            type="button"
            onClick={() => setActiveStorefrontId(null)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
              activeStorefrontId === null
                ? "bg-slate-900 text-white"
                : "border border-slate-200 text-muted-foreground"
            }`}
          >
            전체 물품
          </button>
          {storefronts.map((sf) => (
            <button
              key={sf.storefrontId}
              type="button"
              onClick={() => setActiveStorefrontId(sf.storefrontId)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                activeStorefrontId === sf.storefrontId
                  ? "bg-emerald-600 text-white"
                  : "border border-slate-200 text-muted-foreground"
              }`}
            >
              {sf.name}
              <span className="ml-1 tabular-nums">
                {sf.pricedItemCount}/{sf.allowedItemCount}
              </span>
              {sf.allowedItemCount > sf.pricedItemCount ? (
                <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[10px] text-amber-800">
                  가격 미입력{" "}
                  {(sf.allowedItemCount - sf.pricedItemCount).toLocaleString(
                    "ko-KR",
                  )}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      )}

      {activeStorefront ? (
        <div className="flex-1 overflow-y-auto bg-slate-50 px-3 py-4 sm:px-6">
          <StorefrontOfferingsPanel
            storefrontId={activeStorefront.storefrontId}
            storefrontName={activeStorefront.name}
            includeVat={activeStorefront.includeVat}
          />
        </div>
      ) : (
      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-slate-50">
        {/* 검색 행 (카테고리 ↔ 헤더 사이) — 흰색, 컴팩트 */}
        <div className="bg-background border-b border-slate-200 px-3 py-3 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-2 items-start lg:items-center">
            <div className="w-full lg:flex-1">
              <ProductSearchBar
                className="h-10"
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchClear={() => setSearchTerm("")}
                selectedCategoryName={
                  selectedCategory && selectedCategory.id !== "1"
                    ? selectedCategory.name
                    : null
                }
                onCategoryClear={() => setSelectedCategoryId("1")}
                isFetching={isProductsFetching}
                placeholder={
                  selectedCategory && selectedCategory.id !== "1"
                    ? `'${selectedCategory.name}' 내에서 검색...`
                    : "전체 상품에서 검색 (상품명, 브랜드, 코드)"
                }
              />
              {isSearchTooShort ? (
                <p role="status" className="mt-1 text-xs text-amber-700">
                  2글자 이상 입력해주세요.
                </p>
              ) : null}
            </div>

            {/* 브랜드 필터 — 검색창과 같은 높이의 드롭다운 */}
            {availableBrands.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    id="tour-brand-filter"
                    type="button"
                    className="h-10 shrink-0 flex items-center gap-2 px-3 rounded-xl border border-muted-foreground/20 bg-muted/20 shadow-sm text-sm hover:bg-background transition-colors w-full lg:w-[160px] justify-between"
                  >
                    <span className="text-muted-foreground shrink-0">
                      브랜드
                    </span>
                    <span
                      className={cn(
                        "truncate font-medium",
                        selectedBrands.size === 0 && "text-muted-foreground",
                      )}
                    >
                      {brandLabel}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-60 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-64 p-0">
                  <div className="p-2 border-b">
                    <p className="mb-1.5 text-[11px] text-muted-foreground">
                      지금 보이는 쪽에 있는 브랜드입니다.
                    </p>
                    <Input
                      placeholder="브랜드 검색..."
                      value={brandSearch}
                      onChange={(e) => setBrandSearch(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <ScrollArea className="max-h-64">
                    <div className="p-1">
                      {filteredBrands.length === 0 ? (
                        <p className="px-2 py-4 text-center text-xs text-muted-foreground">
                          검색 결과 없음
                        </p>
                      ) : (
                        filteredBrands.map((brand) => (
                          <button
                            key={brand}
                            type="button"
                            onClick={() => toggleBrand(brand)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent text-sm text-left"
                          >
                            <Checkbox
                              checked={selectedBrands.has(brand)}
                              className="pointer-events-none"
                            />
                            <span className="truncate">{brand}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                  {selectedBrands.size > 0 && (
                    <div className="p-2 border-t">
                      <button
                        type="button"
                        onClick={() => setSelectedBrands(new Set())}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        전체 해제 ({selectedBrands.size})
                      </button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* 회색 배경 영역: 헤더(투명) + 상품 카드 */}
        <div className="flex-1 flex flex-col min-h-0 gap-3 px-3 py-3 sm:px-6 sm:py-4">
          {/* 헤더 — 회색 배경 위, 투명 */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
            {/* 왼쪽: 제목 + 개수 │ 필터(내 상품만 보기 · 판매상태) — 박스 없이 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2 shrink-0">
                  <span className="truncate">{titleText}</span>
                  <Badge
                    variant="default"
                    data-testid="product-count"
                    className="ml-1 rounded-full px-2 text-xs font-medium shrink-0 !bg-primary/10 !text-primary-text hover:!bg-primary/15 border-transparent shadow-none"
                  >
                    {!isPreview && isProductsFetching
                      ? "..."
                      : isBrandFiltered
                        ? `이 쪽 ${products.length.toLocaleString()}개 중 ${visibleProducts.length.toLocaleString()}개`
                        : `${totalCount.toLocaleString()}개`}
                  </Badge>
                </h2>

                {/* 구분선 */}
                <div className="w-px h-5 bg-slate-300 shrink-0 hidden sm:block" />

                {/* 내 상품만 보기 (박스 없음) */}
                <div
                  id="tour-my-products-filter"
                  className="flex items-center gap-1.5 shrink-0"
                >
                  <Switch
                    id="show-my-products-filter"
                    // 판매중·판매중지로 거르는 동안은 내 상품만 본다 — 스위치도 그렇게 보인다.
                    checked={queryMyProducts}
                    // 다시 불러오는 동안은 못 끄고 켠다. 이 스위치는 서버 질의 조건이라
                    // 연달아 누르면 응답이 뒤섞여 «켰는데 안 켜진» 상태로 끝난다.
                    disabled={isFilterBusy || isSellingFiltered}
                    title={
                      isSellingFiltered
                        ? "판매상태로 거르는 동안은 내 상품만 봅니다."
                        : undefined
                    }
                    onCheckedChange={setShowOnlyMyProducts}
                  />
                  <Label
                    htmlFor="show-my-products-filter"
                    className="text-xs cursor-pointer whitespace-nowrap font-medium"
                  >
                    내 상품만 보기
                  </Label>
                  {/*
                    표는 아래에서 스켈레톤을 그리지만, 이 스위치를 누른 사람의 눈은
                    스위치에 있다. 손이 닿은 자리에 응답이 없으면 느린 게 아니라
                    «먹통» 으로 읽힌다.
                  */}
                  {isFilterBusy ? (
                    <Loader2
                      aria-label="불러오는 중"
                      className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground"
                    />
                  ) : null}
                </div>

                {/* 판매상태 (박스 없음) */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                    판매상태
                  </span>
                  <RadioGroup
                    value={sellingFilter}
                    onValueChange={(v) =>
                      isSellingFilter(v) && setSellingFilter(v)
                    }
                    className="flex items-center gap-2"
                  >
                    {[
                      { v: "all", label: "전체" },
                      { v: "selling", label: "판매중" },
                      { v: "stopped", label: "판매중지" },
                    ].map((opt) => (
                      <label
                        key={opt.v}
                        className="flex items-center gap-1 cursor-pointer text-xs whitespace-nowrap"
                      >
                        <RadioGroupItem value={opt.v} className="h-3 w-3" />
                        {opt.label}
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                {baseDescription}
              </p>
              {filterNotes.length > 0 ? (
                <p
                  data-testid="filter-notes"
                  className="text-xs text-primary-text mt-0.5"
                >
                  {filterNotes.join(" ")}
                </p>
              ) : null}
            </div>

            {/* 오른쪽: 요청 내역 / 신규 상품 요청 / 밀도.
                좁은 화면에서는 한 줄에 다 못 들어가므로 줄바꿈시킨다 — `shrink-0` 만 두면
                가로로 삐져나가 페이지 전체에 가로 스크롤이 생긴다. */}
            <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 shadow-sm h-8"
                onClick={() => setIsHistoryOpen(true)}
              >
                <List className="w-4 h-4" />
                요청 내역
              </Button>
              {/*
                관리자에게 받은 단가 엑셀을 올리는 입구. 여기 말고는 단가를 한꺼번에 넣을
                방법이 없다 — 지금까지는 견적을 낼 때 한 건씩만 쌓였다.
              */}
              <Button
                variant="outline"
                size="sm"
                className="gap-2 shadow-sm h-8"
                onClick={() => setIsPriceUploadOpen(true)}
              >
                <FileSpreadsheet className="w-4 h-4" />
                단가 엑셀 등록
              </Button>
              <Button
                variant="default"
                size="sm"
                className="gap-2 shadow-sm h-8"
                onClick={() => setIsSupplierRequestOpen(true)}
              >
                <Plus className="w-4 h-4" />
                신규 상품 요청
              </Button>

              {/* 구분선 */}
              <div className="w-px h-6 bg-slate-300 mx-1 hidden sm:block" />

              {/* Density Toggle */}
              <ToggleGroup
                type="single"
                value={density}
                onValueChange={(v) => isDensity(v) && setDensity(v)}
                variant="outline"
              >
                <ToggleGroupItem
                  value="default"
                  className="px-3 h-8"
                  aria-label="보통 (Default)"
                >
                  <AlignJustify className="w-3.5 h-3.5 md:mr-1.5" />
                  <span className="hidden md:inline text-xs font-medium">
                    보통
                  </span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="compact"
                  className="px-3 h-8"
                  aria-label="좁게 (Compact)"
                >
                  <AlignLeft className="w-3.5 h-3.5 md:mr-1.5" />
                  <span className="hidden md:inline text-xs font-medium">
                    좁게
                  </span>
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {/* 상품 카드: 전체 선택 + 테이블 + 페이지네이션을 하나의 박스로 */}
          <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* 전체 선택 줄 */}
            <div className="flex items-center justify-between gap-3 px-4 py-2 border-b bg-card/60 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={
                    visibleProducts.length > 0 &&
                    visibleProducts.every((p) => selectedIds.has(p.id))
                      ? true
                      : visibleProducts.some((p) => selectedIds.has(p.id))
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(c) => toggleSelectAll(c === true)}
                  aria-label="전체 선택"
                />
                <span className="font-medium">
                  전체 선택 ({visibleProducts.length})
                </span>
                {selectedCount > 0 && (
                  <Badge
                    variant="default"
                    className="h-5 px-2 text-2xs rounded-full !bg-primary/10 !text-primary-text hover:!bg-primary/15 border-transparent shadow-none"
                  >
                    {selectedCount}개 선택됨
                  </Badge>
                )}
              </div>
            </div>

            <VendorProductTable
              storefronts={storefronts}
              storefrontPriceOf={priceOf}
              storefrontIsAllowed={isStorefrontAllowed}
              onStorefrontRequest={requestOffering}
              onStorefrontPriceChange={setStorefrontPrice}
              products={visibleProducts}
              isLoading={!isPreview && isProductsFetching}
              showOnlyMyProducts={showOnlyMyProducts}
              density={density}
              searchTerm={effectiveSearch}
              pendingTogglingProductIds={pendingTogglingProductIds}
              onPriceChange={handlePriceChange}
              onToggleSelling={toggleSelling}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              emptyState={emptyState}
            />

            {/* 하단: 설명글 + 페이지네이션. 좁은 화면에서는 두 줄로 눕힌다 —
                한 줄로 두면 페이지네이션이 밀려나 눌리지 않는다. */}
            <div className="flex flex-col gap-2 border-t border-primary/15 bg-primary/5 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-4">
              <div className="flex items-start gap-1.5 text-2xs leading-snug text-primary-pressed">
                <Info className="w-3 h-3 shrink-0 mt-0.5" />
                <p>
                  소비자 단가는 시장 표준 단가입니다. 실제 납품하실 단가(‘나의
                  공급가(VAT 별도)’)를 원 단위로 입력하고 ‘판매 상태’를
                  켜주세요. 키보드 위/아래 방향키(↑, ↓)로 단가 입력창을 빠르게
                  이동할 수 있습니다.
                </p>
              </div>
              <div className="flex shrink-0 justify-center sm:justify-end">
                <AdvancedPagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            </div>
          </div>
          {/* /상품 카드 */}
        </div>
        {/* /회색 배경 영역 */}
      </div>
      )}

      {/* Supplier Item Request Dialog */}
      {isSupplierRequestOpen && (
        <SupplierItemRequestDialog
          isOpen={isSupplierRequestOpen}
          onOpenChange={setIsSupplierRequestOpen}
          initialCategoryId={
            selectedCategoryId && selectedCategoryId !== "1"
              ? selectedCategoryId
              : undefined
          }
        />
      )}

      {isHistoryOpen && (
        <SupplierRequestHistoryDialog
          isOpen={isHistoryOpen}
          onOpenChange={setIsHistoryOpen}
        />
      )}

      {isPriceUploadOpen && (
        <SupplierPriceUploadDialog
          isOpen={isPriceUploadOpen}
          onOpenChange={setIsPriceUploadOpen}
        />
      )}

      {isModificationDialogOpen && (
        <ProductModificationDialog
          isOpen={isModificationDialogOpen}
          onClose={() => setIsModificationDialogOpen(false)}
          product={modificationTargetProduct}
          onSubmit={() => {
            if (modificationTargetProduct) {
              toast.success("수정 요청이 전송되었습니다.");
            }
          }}
        />
      )}
    </div>
  );
};
